import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, from, Observable, of, throwError } from 'rxjs';
import { catchError, delay, retry, switchMap } from 'rxjs/operators';
import { ApiService } from '../services/api.service';
import { CookieService } from 'ngx-cookie-service';
import { CookieConsentService } from '../services/cookie-consent.service';
import { TokenService } from '../services/token.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {

    token: string;
    isRefreshing: boolean = false;

    constructor(
        private router: Router,
        private cookieService: CookieService,
        private consentService: CookieConsentService,
        private apiService: ApiService,
        private tokenService: TokenService,
    ) { }

    public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {

        if (this.token) {
            if (this.tokenService.isTokenExpiringSoon() && !this.isRefreshing) {
                this.isRefreshing = true;

                return this.apiService.refreshLoginForUser().pipe(delay(2000),
                    switchMap((response: any) => {
                        this.isRefreshing = false;
                        this.tokenService.getToken().then();
                        this.apiService.refreshProfileInformationSubject.next(true);

                        request = request.clone({ withCredentials: true, });
                        return next.handle(request);
                    }),
                    catchError(error => {
                        this.isRefreshing = false;
                        return throwError(error);
                    })
                );
            }
        }

        request = request.clone({
            withCredentials: true,
        });

        return next.handle(request)
            .pipe(
                retry(0),
                catchError((error: HttpErrorResponse | any) => {
                    if (error && error.message === 'Unauthorized' && error.statusCode === 401) {
                        return from(this.handle401Error(request, next));
                    } else if (error && error.message === 'Token is missing!') {
                        return from(this.handleMissingTokenError(request, next));
                    } else if (error && error.error && error.error.message === 'Unauthorized'
                        && error.error.statusCode === 401) {
                        return from(this.handle401Error(request, next));
                    } else if (error && error.error && error.error.message === 'INVALID_TOKEN' &&
                        error.error.statusCode === 401) {
                        return from(this.handle401Error(request, next));
                    } else if (error && error.error && error.error.message === 'ACCESS_NOT_GRANTED' &&
                        error.error.statusCode === 401) {
                        return from(this.handle401Error(request, next));
                    } else if (error && error.error && error.error.message === 'Forbidden resource' &&
                        error.error.statusCode === 403) {
                        return from(this.handle401Error(request, next));
                    } else if (error.error) {
                        try {
                            const parseError = JSON.parse(error.error);
                            return throwError(parseError);
                        } catch {
                            return throwError(error.error);
                        }
                    } else {
                        return throwError(error);
                    }
                }),
            );
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
        return this.apiService.refreshLoginForUser().pipe(
            delay(2000),
            switchMap((response: any) => {
                request = request.clone({
                    withCredentials: true,
                });

                return next.handle(request);
            }),
            catchError(error => {
                return throwError(error);
            })
        );
    }

    private handleMissingTokenError(request: HttpRequest<any>, next: HttpHandler) {
        return this.apiService.refreshLoginForUser().pipe(
            delay(2000),
            switchMap((response: any) => {
                return next.handle(request);
            }),
            catchError(error => {
                return throwError(error);
            })
        );
    }
}
